import type { PickedHealthDocument } from '@meddihub/mzcr-ui';
import apiClient from '../../../Services/ApiClient';
import { HealthDocumentationFileRef } from '../../Ecard/Interfaces/HealthDocumentationFileRef.interface';
import { blobToBase64 } from '../Utils/base64.util';
import { mapWithConcurrency } from '../Utils/mapWithConcurrency.util';

/** How many files are downloaded at once. */
const DOWNLOAD_CONCURRENCY = 3;

/** `download` query parameter of the documentation file endpoint: 1 = preview, 2 = attachment. */
const DOWNLOAD_AS_ATTACHMENT = 2;

const GENERIC_MIME_TYPE = 'application/octet-stream';

/** Used only when the server does not say what the file is. */
const MIME_TYPES_BY_EXTENSION: Record<string, string> = {
	pdf: 'application/pdf',
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	png: 'image/png',
	gif: 'image/gif',
	txt: 'text/plain',
	xml: 'text/xml',
	doc: 'application/msword',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	zip: 'application/zip',
	dcm: 'application/dicom',
};

const resolveMimeType = (blob: Blob, extension: string) =>
	blob.type && blob.type !== GENERIC_MIME_TYPE
		? blob.type
		: MIME_TYPES_BY_EXTENSION[extension] ?? GENERIC_MIME_TYPE;

/**
 * Downloads files of the patient's health documentation in the shape mzcr-ui takes for
 * a new package (`PickedHealthDocument`) - content as base64.
 */
const downloadHealthDocuments = (
	patientId: string,
	files: HealthDocumentationFileRef[],
): Promise<PickedHealthDocument[]> =>
	mapWithConcurrency(files, DOWNLOAD_CONCURRENCY, async file => {
		const response = await apiClient.get(
			`/patients/${patientId}/documentation/${file.folderId}/${file.id}?download=${DOWNLOAD_AS_ATTACHMENT}`,
			{ responseType: 'blob', catchError: false },
		);
		const blob = response.data as Blob;
		const extension = file.extension.toLowerCase();
		const fileName = file.name.toLowerCase().endsWith(`.${extension}`)
			? file.name
			: `${file.name}${extension ? `.${extension}` : ''}`;
		return {
			fileName,
			mimeType: resolveMimeType(blob, extension),
			contentBase64: await blobToBase64(blob),
			size: blob.size,
			folderName: file.folderName,
		};
	});

export default downloadHealthDocuments;
